import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { ArrowRight, Bike, MapPin, MessageCircle, Activity } from "lucide-react";

export default function Landing() {
  return (
    <>
      <Navbar />
      <main className="relative">
        {/* Hero */}
        <section className="relative min-h-[82vh] overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: "url(https://images.unsplash.com/photo-1517649763962-0c623066013b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920)" }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-white/30" />

          <div className="relative max-w-7xl mx-auto px-4 md:px-8 pt-20 md:pt-32 pb-20">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-px w-12 bg-[#E11D48]" />
                <span className="label-tag text-[#E11D48]">Bicyclub · Beta</span>
              </div>
              <h1 className="font-display font-black text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.95] text-gray-900">
                Trova il tuo<br />
                <span className="text-[#E11D48]">gruppo</span>.<br />
                Pubblica il tuo giro.
              </h1>
              <p className="mt-8 text-lg md:text-xl text-gray-700 max-w-2xl leading-relaxed">
                Il primo sito per chi vuole pedalare in compagnia. Pubblica il tuo prossimo giro,
                trova chi pedala con te, coordina partenza e ritmo nella chat dedicata.
              </p>
              <div className="mt-10 flex flex-wrap gap-3">
                <Link to="/register" data-testid="hero-cta-register" className="btn-primary px-8 py-4 rounded-md flex items-center gap-2 text-sm">
                  Iscriviti <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/login" data-testid="hero-cta-login" className="px-8 py-4 border border-gray-300 hover:border-gray-900 text-gray-900 rounded-md font-bold tracking-wider uppercase text-sm bg-white/70 backdrop-blur">
                  Accedi
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="max-w-7xl mx-auto px-4 md:px-8 py-24">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <div className="label-tag mb-3">Come funziona</div>
              <h2 className="font-display font-black text-4xl md:text-5xl uppercase tracking-tighter">
                <span className="text-[#E11D48]">Tre passi.<br />Zero attriti.</span>
              </h2>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Bike, k: "publish", t: "Pubblica il giro", d: "Data, orario (anche flessibile), distanza, dislivello, tipo bici, percorso e fondo. In meno di 60 secondi." },
              { icon: MapPin, k: "filter", t: "Filtra per zona", d: "Imposta la tua posizione e il raggio. Vediamo solo i giri della tua area, in fasce orarie compatibili." },
              { icon: MessageCircle, k: "chat", t: "Coordina e parti", d: "Chat dedicata per ogni giro. Pulsante un-clic per partecipare. Niente più gruppi WhatsApp infiniti." },
            ].map(({ icon: Icon, k, t, d }) => (
              <div key={k} className="tactical-card rounded-sm p-8">
                <Icon className="w-8 h-8 text-[#E11D48]" strokeWidth={1.5} />
                <h3 className="font-display font-bold text-2xl mt-6 text-gray-900">{t}</h3>
                <p className="text-gray-500 mt-3 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Integrations */}
        <section className="max-w-7xl mx-auto px-4 md:px-8 py-24 border-t border-gray-200">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="label-tag mb-3 text-[#E11D48]">Integrazioni</div>
              <h2 className="font-display font-black text-4xl md:text-5xl uppercase tracking-tighter text-gray-900">
                Strava.<br />Komoot.
              </h2>
              <p className="text-gray-500 mt-6 text-lg leading-relaxed">
                Collega i tuoi account per sincronizzare la tua potenza, ore in sella e tracce.
                Rendi il tuo profilo riconoscibile, trova compagni adatti al tuo ritmo.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <span className="px-4 py-3 bg-[#FC4C02] text-white font-bold uppercase tracking-wider text-xs rounded-sm">Strava</span>
                <span className="px-4 py-3 bg-[#6AA127] text-white font-bold uppercase tracking-wider text-xs rounded-sm">Komoot</span>
              </div>
            </div>
            <div className="tactical-card rounded-sm p-8">
              <div className="flex items-center gap-3 mb-6">
                <Activity className="w-5 h-5 text-[#E11D48]" />
                <span className="label-tag">Live · Profilo demo</span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="label-tag mb-1">20min</div>
                  <div className="font-display font-black text-4xl">280<span className="text-base text-gray-500"> W</span></div>
                </div>
                <div>
                  <div className="label-tag mb-1">Ultimi 30g</div>
                  <div className="font-display font-black text-4xl">25<span className="text-base text-gray-500"> h</span></div>
                </div>
                <div>
                  <div className="label-tag mb-1">Bici preferite</div>
                  <div className="font-display font-bold text-2xl">BDC + Gravel</div>
                </div>
                <div>
                  <div className="label-tag mb-1">Velocità avg</div>
                  <div className="font-display font-black text-4xl">28<span className="text-base text-gray-500"> km/h</span></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <footer className="border-t border-gray-200 py-10 text-center text-xs uppercase tracking-[0.2em] text-gray-400 space-y-3">
          <div className="flex justify-center gap-6">
            <Link to="/privacy" className="hover:text-gray-700" data-testid="footer-privacy">Privacy</Link>
            <Link to="/terms" className="hover:text-gray-700" data-testid="footer-terms">Termini</Link>
          </div>
          <div>© 2026 Bicyclub · Made in Italy con ❤️ per chi pedala</div>
        </footer>
      </main>
    </>
  );
}
